import type { ReactNode } from 'react'
import AirRoundedIcon from '@mui/icons-material/AirRounded'
import FloodRoundedIcon from '@mui/icons-material/FloodRounded'
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded'
import WaterDropRoundedIcon from '@mui/icons-material/WaterDropRounded'
import WbSunnyRoundedIcon from '@mui/icons-material/WbSunnyRounded'
import AutoGraphRoundedIcon from '@mui/icons-material/AutoGraphRounded'
import { CanonicalPeriod, PERIODS } from './index'
import type { MetricScenarioType } from '../../../hooks/analytics'

export const DEFAULT_SCENARIO_PREFERENCE = 'default'

export type HazardScenarioPreferenceValue = MetricScenarioType | typeof DEFAULT_SCENARIO_PREFERENCE

export type HazardScenarioPreferenceOption = {
  value: HazardScenarioPreferenceValue
  label: string
}

export type HazardFilterEditorConfig = {
  scenarioSelectionMode: 'single' | 'multiple'
  showReturnPeriodFilters: boolean
  scenarioFieldLabel?: string
  singleScenarioOptions?: HazardScenarioPreferenceOption[]
}

export type HazardScenarioPreferencesConfig = HazardFilterEditorConfig

export type HazardViewConfig = {
  availablePeriods: CanonicalPeriod[]
  defaultPeriod: CanonicalPeriod
  scenarioPreferences?: HazardScenarioPreferencesConfig
  /** Whether this hazard has an impact model that can produce adaptation-adjusted results. */
  supportsAdaptations?: boolean
}

export type HazardVisualMeta = {
  icon: ReactNode
  tone: string
  accent: string
}

export type HazardMethodologySection = {
  /** Slug used as DOM id and URL hash; must match the auto-slug produced by rehype-slug for the heading text. */
  id: string
  /** Human-readable label shown in the in-drawer / in-page table of contents. */
  label: string
}

export type HazardMethodologyConfig = {
  /** Display name used in the drawer header and standalone page title. */
  label: string
  /** Path under /methodology/ (served from frontend/public/methodology) to the markdown file. */
  docPath: string
  /** Curated top-level sections for the table of contents and deep-link targets. */
  sections: HazardMethodologySection[]
}

const multiPeriodHazardConfig: HazardViewConfig = {
  availablePeriods: PERIODS,
  defaultPeriod: CanonicalPeriod.LONG_TERM,
}

export const climateHazardViewConfigByCode: Record<string, HazardViewConfig> = {
  fld: {
    ...multiPeriodHazardConfig,
    supportsAdaptations: true,
    scenarioPreferences: {
      scenarioSelectionMode: 'single',
      showReturnPeriodFilters: false,
      scenarioFieldLabel: 'Scenario source',
      singleScenarioOptions: [
        { value: DEFAULT_SCENARIO_PREFERENCE, label: 'Default (SSP first)' },
        { value: 'rcp', label: 'RCP only' },
      ],
    },
  },
  hs: multiPeriodHazardConfig,
  tc: {
    availablePeriods: [CanonicalPeriod.LONG_TERM],
    defaultPeriod: CanonicalPeriod.LONG_TERM,
    supportsAdaptations: true,
  },
  wf: {
    ...multiPeriodHazardConfig,
    supportsAdaptations: true,
  },
  wr: multiPeriodHazardConfig,
}

const fallbackHazardViewConfig: HazardViewConfig = multiPeriodHazardConfig

const climateHazardVisualMetaByCode: Record<string, HazardVisualMeta> = {
  fld: {
    icon: <FloodRoundedIcon fontSize="small" />,
    tone: '#0f3d5e',
    accent: '#9fd0f4',
  },
  hs: {
    icon: <WbSunnyRoundedIcon fontSize="small" />,
    tone: '#a15c00',
    accent: '#ffd37a',
  },
  tc: {
    icon: <AirRoundedIcon fontSize="small" />,
    tone: '#115e59',
    accent: '#97f0e3',
  },
  wf: {
    icon: <LocalFireDepartmentRoundedIcon fontSize="small" />,
    tone: '#7f1d1d',
    accent: '#f7b0a9',
  },
  wr: {
    icon: <WaterDropRoundedIcon fontSize="small" />,
    tone: '#0f4c81',
    accent: '#a8dcff',
  },
}

const fallbackHazardVisualMeta: HazardVisualMeta = {
  icon: <AutoGraphRoundedIcon fontSize="small" />,
  tone: '#334155',
  accent: '#cbd5e1',
}

/**
 * Methodology documentation metadata per hazard.
 *
 * - `docPath` is served from `frontend/public/methodology/` (populated at build
 *   time by `scripts/copy-methodology.js`, which mirrors `docs/climate/`).
 * - `sections[].id` values are the slugs that `rehype-slug` produces from the
 *   corresponding `## Heading` text in each markdown file. They are also used
 *   as URL hashes (`/methodology/:hazard#<id>`) for deep linking from
 *   dashboards.
 */
const climateHazardMethodologyByCode: Record<string, HazardMethodologyConfig> = {
  fld: {
    label: 'Flood',
    docPath: 'flood/flood-methodology.md',
    sections: [
      { id: 'our-data-and-models', label: 'Our data and models' },
      { id: 'from-flood-depths-to-financial-impact', label: 'From flood depths to financial impact' },
      { id: 'business-interruptions', label: 'Business Interruptions' },
    ],
  },
  hs: {
    label: 'Heat Stress',
    docPath: 'heat-stress/heat-stress-methodology.md',
    sections: [],
  },
  tc: {
    label: 'Extreme Wind (Tropical Cyclone)',
    docPath: 'tropical-cyclone/tropical-cyclone-methodology.md',
    sections: [
      { id: 'data-and-modelling-approach', label: 'Data and modelling approach' },
      { id: 'translating-wind-speeds-into-damages', label: 'Translating wind speeds into damages' },
      { id: 'business-interruptions', label: 'Business Interruptions' },
      { id: 'risk-score', label: 'Risk score' },
    ],
  },
  wf: {
    label: 'Wildfire',
    docPath: 'wildfire/wildfire-methodology.md',
    sections: [
      { id: 'modelling-approach', label: 'Modelling approach' },
      { id: 'estimating-potential-impacts', label: 'Estimating potential impacts' },
      { id: 'risk-score', label: 'Risk score' },
    ],
  },
  wr: {
    label: 'Water Stress',
    docPath: 'water-stress/water-stress-methodology.md',
    sections: [],
  },
}

export function getClimateHazardViewConfig(hazardCode?: string | null): HazardViewConfig {
  if (!hazardCode) return fallbackHazardViewConfig
  return climateHazardViewConfigByCode[hazardCode] ?? fallbackHazardViewConfig
}

export function getClimateHazardVisualMeta(hazardCode?: string | null): HazardVisualMeta {
  if (!hazardCode) return fallbackHazardVisualMeta
  return climateHazardVisualMetaByCode[hazardCode] ?? fallbackHazardVisualMeta
}

export function getClimateHazardMethodology(hazardCode?: string | null): HazardMethodologyConfig | null {
  if (!hazardCode) return null
  return climateHazardMethodologyByCode[hazardCode] ?? null
}

export function getAllClimateHazardMethodologies(): Array<{ code: string; config: HazardMethodologyConfig }> {
  return Object.entries(climateHazardMethodologyByCode).map(([code, config]) => ({ code, config }))
}
